import { useEffect, useState } from "react";

/**
 * The phone's cache of venue documents and board snapshots (SCORZA-739 plan §5.3).
 *
 * This replaces a plain 5-minute TTL `Map`. The TTL was the wrong instrument: it made a board
 * that had just been rebuilt for *your own* match unreadable for up to five minutes, while doing
 * nothing about a board that had gone stale for some other reason. What actually makes a board
 * stale for this user is an event they caused — their own submission — and that is something we
 * can be told about rather than having to guess at with a clock.
 *
 * So there are two mechanisms, and they do different jobs:
 *
 *  - **Invalidate on event.** `invalidateVenue` is called after a 202 for one of my matches. It
 *    drops that venue's two keys and notifies subscribers, so an already-mounted stats tab
 *    refetches instead of rendering the board from before this match.
 *  - **Stale-while-revalidate.** A cached value renders immediately and is refetched in the
 *    background once it is older than `STALE_AFTER_MS`. That covers everyone else's matches,
 *    where nothing tells us anything, without ever making the user wait on a spinner for data
 *    we already have.
 *
 * In memory only, and deliberately not persisted: a board is ~60 KB and goes stale, whereas the
 * `venueRef` on the match doc — the thing that actually has to survive a relaunch — is already
 * persisted with the match. Unbounded in practice; a user visits a handful of grounds.
 *
 * **Nothing in here imports `venueService`, and it must stay that way.** `venueService` imports
 * `invalidateVenue` (it drops a venue's keys after a 202), so a loader living here would close
 * the loop — and a cycle through a module this early in the import graph resolves to `undefined`
 * at call time, which surfaces as the whole match page falling into its error boundary rather
 * than as an import error. The fetches are passed in by the caller; `useVenueBoard` owns the
 * pairing of a key with its loader.
 */

/** How old a cached value may be before a render also kicks off a background refetch. */
export const STALE_AFTER_MS = 2 * 60 * 1000;

type Loader<T> = () => Promise<T>;

interface Slot<T> {
    /** Resolved value, once there is one. Present even while a revalidation is in flight. */
    value?: T;
    at: number;
    inFlight?: Promise<T>;
}

const slots = new Map<string, Slot<unknown>>();
const listeners = new Set<() => void>();

/** Bumped on every mutation, so `useSyncExternalStore` has a cheap immutable snapshot to diff. */
let version = 0;

function notify(): void {
    version++;
    listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

function getVersion(): number {
    return version;
}

/**
 * Re-renders the caller whenever anything in the store changes.
 *
 * Hand-rolled rather than `useSyncExternalStore`: the app runs React 18 but is still on
 * `@types/react` 16, so the hook exists at runtime and not in the type definitions. A version
 * counter in `useState` is equivalent for a store that only ever moves forwards, and the
 * re-subscribe on mount re-reads it so nothing published between render and effect is missed.
 */
export function useVenueStoreVersion(): number {
    const [, setSeen] = useState(getVersion);

    useEffect(() => {
        setSeen(getVersion());
        return subscribe(() => setSeen(getVersion()));
    }, []);

    return getVersion();
}

export function venueKey(venueId: string): string {
    return `venue:${venueId}`;
}

export function boardKey(venueId: string): string {
    return `board:${venueId}`;
}

export function ribbonsKey(venueId: string, matchId: string): string {
    return `ribbons:${venueId}:${matchId}`;
}

/** Three decimal places is ~110 m — the same key space refauth's `/near` lookup works in. */
export function nearKey(latitude: number, longitude: number): string {
    return `near:${latitude.toFixed(3)},${longitude.toFixed(3)}`;
}

export function peek<T>(key: string): T | undefined {
    return slots.get(key)?.value as T | undefined;
}

export function isStale(key: string, now: number = Date.now()): boolean {
    const slot = slots.get(key);
    return !slot || now - slot.at >= STALE_AFTER_MS;
}

/**
 * Fetch `key` through the cache, stale-while-revalidate, sharing one in-flight request between
 * concurrent callers.
 *
 * Three cases, in the order they are tested:
 *
 *  1. A request is already in flight — join it. This is what collapses the result page's ribbons
 *     and the venue page the user taps through to into one round trip.
 *  2. A cached value that is still fresh — resolve it immediately, no network.
 *  3. A cached value that has gone stale — resolve it immediately *and* refetch in the
 *     background. The refetch notifies on arrival, which re-renders subscribers through
 *     `useVenueStoreVersion`. Nothing ever waits on a spinner for data we already hold, and a
 *     rejection from a background refetch is swallowed because the visible value is unaffected.
 *
 * This also covers "the app came back to the foreground": anything older than `STALE_AFTER_MS`
 * revalidates on the first render after the resume, so no app-state listener is needed.
 *
 * `force` (pull-to-refresh) waits for fresh data rather than resolving the stale value, but still
 * joins an in-flight request rather than starting a second one.
 *
 * A rejection is never cached: the slot keeps whatever value it had, so a transient failure does
 * not blank a board that is on screen, and the next caller retries.
 */
export function load<T>(
    key: string,
    loader: Loader<T>,
    force = false
): Promise<T> {
    const slot = slots.get(key) as Slot<T> | undefined;

    if (slot?.inFlight) {
        return slot.inFlight;
    }

    const hasValue = slot !== undefined && slot.value !== undefined;
    if (!force && hasValue && !isStale(key)) {
        return Promise.resolve(slot!.value as T);
    }

    const inFlight = loader()
        .then((value) => {
            slots.set(key, { value, at: Date.now() });
            notify();
            return value;
        })
        .catch((error) => {
            const previous = slots.get(key) as Slot<T> | undefined;
            if (previous) {
                slots.set(key, { ...previous, inFlight: undefined });
            }
            throw error;
        });

    slots.set(key, { ...(slot || { at: 0 }), inFlight });

    if (!force && hasValue) {
        // Revalidating behind a value we can already show.
        inFlight.catch(() => {});
        return Promise.resolve(slot!.value as T);
    }
    return inFlight;
}

/**
 * Drop everything the store knows about one venue and tell subscribers.
 *
 * Called after a successful submission for one of my own matches, which is the one moment we
 * know for certain that this venue's board has moved. `/venues/near` results are left alone:
 * they carry `entryCount` and `playerCount`, which do drift, but a stale count in a list of
 * nearby grounds is not worth a refetch on every match finish.
 */
export function invalidateVenue(venueId: string): void {
    slots.delete(venueKey(venueId));
    slots.delete(boardKey(venueId));
    // A new entry here can change the ribbons of every one of my matches at this venue.
    const ribbonsPrefix = ribbonsKey(venueId, "");
    Array.from(slots.keys())
        .filter((key) => key.startsWith(ribbonsPrefix))
        .forEach((key) => slots.delete(key));
    notify();
}

/** Drops everything, so a pull-to-refresh genuinely refetches. */
export function clearVenueStore(): void {
    slots.clear();
    notify();
}
